import axios from 'axios';
import {readStoredSid} from '@api/auth';
import {adaptAnalyticsInsight} from '@api/analytics-insights';
import type {
  AnalyticsInsightCard,
  AnalyticsInsightCardDTO,
  AnalyticsInsightInclude,
  AnalyticsInsightMyState,
  AnalyticsInsightMyStatePatch,
  AnalyticsInsightsPage,
  AnalyticsInsightsPageDTO,
  AnalyticsInsightsRequest,
} from '@api/analytics-insights';
import type {WeeklyReport, WeeklyReportsPage} from './types';

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? import.meta.env.VITE_DEV_USER_SID;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

/** Контракт reader-scoped сохранённых отчётов. */
export const weeklyReportsClient = {
  async list(
    workspaceSid: string,
    cursor?: string,
  ): Promise<WeeklyReportsPage> {
    const {data} = await http.post<WeeklyReportsPage>(
      `/workspaces/${workspaceSid}/analytics-reports`,
      {limit: 50, ...(cursor && {cursor})},
    );
    return data;
  },
  async get(workspaceSid: string, reportSid: string): Promise<WeeklyReport> {
    const {data} = await http.get<WeeklyReport>(
      `/workspaces/${workspaceSid}/analytics-reports/${reportSid}`,
    );
    return data;
  },
  async insights(
    workspaceSid: string,
    reportSid: string,
    request: AnalyticsInsightsRequest,
  ): Promise<AnalyticsInsightsPage> {
    const {data} = await http.post<AnalyticsInsightsPageDTO>(
      `/workspaces/${workspaceSid}/analytics-reports/${reportSid}/insights`,
      request,
    );
    return {...data, rows: data.rows.map(adaptAnalyticsInsight)};
  },
  async insight(
    workspaceSid: string,
    reportSid: string,
    insightSid: string,
    include: AnalyticsInsightInclude[],
  ): Promise<AnalyticsInsightCard> {
    const params = new URLSearchParams();
    include.forEach((value) => params.append('include', value));
    const {data} = await http.get<AnalyticsInsightCardDTO>(
      `/workspaces/${workspaceSid}/analytics-reports/${reportSid}/insights/${insightSid}`,
      {params},
    );
    return adaptAnalyticsInsight(data);
  },
  async updateInsightMyState(
    workspaceSid: string,
    reportSid: string,
    insightSid: string,
    dto: AnalyticsInsightMyStatePatch,
  ): Promise<AnalyticsInsightMyState> {
    const {data} = await http.patch<AnalyticsInsightMyState>(
      `/workspaces/${workspaceSid}/analytics-reports/${reportSid}/insights/${insightSid}/my-state`,
      dto,
    );
    return data;
  },
};
